"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { propertyRows } from "@/components/tools/number-theory/property-rows";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { exponentForm } from "@/lib/tool-kit/math/primes";
import {
  analyseNumber,
  type Classification,
  type NumberProperties,
  readWholeNumber,
} from "@/lib/tool-kit/number-theory/properties";

/** English strings. A translated content file overrides these through `ui`. */
export const NUMBER_PROPERTIES_CALCULATOR_STRINGS = {
  number: "Whole number",
  numberHint: "From 1 up to 10²⁰. Spaces between digit groups are ignored.",
  results: "Results",
  divisorCount: "Number of divisors",
  divisorSum: "Sum of divisors",
  divisorType: "Divisor type",
  totient: "Euler's totient",
  digits: "Digits",
  digitSum: "Digit sum",
  digitalRoot: "Digital root",
  perfect: "Perfect",
  abundant: "Abundant",
  deficient: "Deficient",
  explainPerfect:
    "The proper divisors (all divisors except {n} itself) add up to {aliquot}, exactly {n}.",
  explainAbundant:
    "The proper divisors (all divisors except {n} itself) add up to {aliquot}, which is {gap} more than {n}.",
  explainDeficient:
    "The proper divisors (all divisors except {n} itself) add up to {aliquot}, which is {gap} less than {n}.",
  properSum: "Proper divisors: {sum} = {aliquot}",
  factorization: "Prime factorization: {n} = {form}",
  tableHeading: "Properties",
  colProperty: "Property",
  colAnswer: "Answer",
  colDetail: "Why",
  yes: "Yes",
  no: "No",
  even: "Even",
  odd: "Odd",
  prime: "Prime",
  composite: "Composite",
  neither: "Neither prime nor composite",
  propParity: "Even or odd",
  propPrimality: "Prime or composite",
  propSquare: "Perfect square",
  propCube: "Perfect cube",
  propPowerOfTwo: "Power of 2",
  propTriangular: "Triangular number",
  propFibonacci: "Fibonacci number",
  propPalindrome: "Palindrome (base 10)",
  propBinaryPalindrome: "Palindrome (binary)",
  propSquarefree: "Squarefree",
  detailEven: "{n} = 2 × {half}",
  detailOdd: "{n} = 2 × {half} + 1",
  detailPrime: "Its only divisors are 1 and {n}.",
  detailNeither:
    "1 has only one divisor, so it is not prime; it has no prime factors, so it is not composite.",
  detailSquareYes: "{n} = {r}²",
  detailSquareNo: "Between {a}² = {sa} and {b}² = {sb}.",
  detailCubeYes: "{n} = {r}³",
  detailCubeNo: "Between {a}³ = {sa} and {b}³ = {sb}.",
  detailPowerYes: "{n} = 2^{e}",
  detailPowerNo: "In binary it has more than one 1: {binary}.",
  detailTriangularYes: "{n} = 1 + 2 + … + {k} = {k}({k} + 1) ÷ 2",
  detailTriangularNo: "Between T({k}) = {a} and T({k1}) = {b}.",
  detailFibonacciYes: "F({k}) = {n}",
  detailFibonacciOne: "F(1) = F(2) = 1",
  detailFibonacciNo: "Between the Fibonacci numbers {a} and {b}.",
  detailPalindromeYes: "It reads the same backwards.",
  detailPalindromeNo: "Backwards it reads {reversed}.",
  detailBinary: "In binary: {binary}",
  detailSquarefreeYes: "No prime factor appears more than once.",
  detailSquarefreeNo: "In {factors} a prime factor repeats.",
  divisorsHeading: "Divisors",
  divisorsList: "All {count} divisors, in order:",
  divisorsTooMany: "There are {count} divisors, too many to list here.",
  incomplete:
    "This number resisted quick factoring, so the divisor figures are left out.",
  copyResult: "Copy summary",
  copied: "Copied!",
  summary:
    "{n} — {parity}; {divisors} divisors; sum of divisors {sigma}; {type}.",
  enter: "Enter a whole number to see its properties.",
  invalid: "Enter a whole number, with no decimals, fractions or commas.",
  zero: "Enter a number of 1 or more. 0 is even, a square and a triangular number, but divisors and primality do not apply to it.",
  negative:
    "Enter a positive whole number. Divisor sums and primality are defined for positive integers.",
  tooLarge: "Enter a whole number no larger than 10²⁰.",
} as const;

type Strings = typeof NUMBER_PROPERTIES_CALCULATOR_STRINGS;

const MAX_PROPER_TERMS = 30;
const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const PROSE_SM = "text-muted-foreground text-sm";

function typeLabel(type: Classification, strings: Strings): string {
  const labels = {
    perfect: strings.perfect,
    abundant: strings.abundant,
    deficient: strings.deficient,
  };
  return labels[type];
}

function explainType(p: NumberProperties, strings: Strings): string {
  if (p.classification === null || p.aliquotSum === null) {
    return "";
  }
  const gap = p.aliquotSum > p.n ? p.aliquotSum - p.n : p.n - p.aliquotSum;
  const templates = {
    perfect: strings.explainPerfect,
    abundant: strings.explainAbundant,
    deficient: strings.explainDeficient,
  };
  return t(templates[p.classification], {
    n: String(p.n),
    aliquot: String(p.aliquotSum),
    gap: String(gap),
  });
}

function DivisorBlock({
  p,
  strings,
}: {
  p: NumberProperties;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  if (p.divisorCount === null) {
    return null;
  }
  const proper = p.divisors?.filter((d) => d !== p.n) ?? [];
  const showSum = proper.length > 0 && proper.length <= MAX_PROPER_TERMS;
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.divisorsHeading}</h3>
      <p className={PROSE_SM}>{explainType(p, strings)}</p>
      {showSum ? (
        <div className={MONO_LINES}>
          <p>
            {t(strings.properSum, {
              sum: proper.map(String).join(" + "),
              aliquot: String(p.aliquotSum),
            })}
          </p>
        </div>
      ) : null}
      {p.divisors ? (
        <>
          <p className={PROSE_SM}>
            {t(strings.divisorsList, { count: fmt(p.divisorCount) })}
          </p>
          <p className="break-words rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
            {p.divisors.map(String).join(", ")}
          </p>
        </>
      ) : (
        <Notice>
          {t(strings.divisorsTooMany, { count: fmt(p.divisorCount) })}
        </Notice>
      )}
    </>
  );
}

function Results({ n, strings }: { n: bigint; strings: Strings }) {
  const fmt = useToolNumber();
  const p = useMemo(() => analyseNumber(n), [n]);
  const complete = p.classification !== null;
  const rows = propertyRows(p, strings);
  const summary = t(strings.summary, {
    n: String(p.n),
    parity: p.even ? strings.even : strings.odd,
    divisors: p.divisorCount === null ? "?" : fmt(p.divisorCount),
    sigma: String(p.divisorSum ?? "?"),
    type: p.classification ? typeLabel(p.classification, strings) : "?",
  });
  return (
    <>
      {complete ? (
        <StatGrid>
          <StatCard
            emphasis
            label={strings.divisorType}
            value={typeLabel(p.classification as Classification, strings)}
          />
          <StatCard
            label={strings.divisorCount}
            value={fmt(p.divisorCount ?? 0)}
          />
          <StatCard label={strings.divisorSum} value={String(p.divisorSum)} />
          <StatCard label={strings.totient} value={String(p.totient)} />
          <StatCard label={strings.digits} value={fmt(p.digits)} />
          <StatCard label={strings.digitSum} value={fmt(p.digitSum)} />
        </StatGrid>
      ) : (
        <ErrorNotice>{strings.incomplete}</ErrorNotice>
      )}
      <p className="text-foreground text-sm">
        {t(strings.factorization, {
          n: String(p.n),
          form: p.n === BigInt(1) ? "1" : exponentForm(p.factorization.factors),
        })}
      </p>
      <p className={PROSE_SM}>
        {strings.digitalRoot}: {fmt(p.digitalRoot)}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[strings.colProperty, strings.colAnswer, strings.colDetail]}
        rows={rows}
      />
      <DivisorBlock p={p} strings={strings} />
    </>
  );
}

function Outcome({ raw, strings }: { raw: string; strings: Strings }) {
  const read = useMemo(() => readWholeNumber(raw), [raw]);
  switch (read.kind) {
    case "ok":
      return <Results n={read.value} strings={strings} />;
    case "empty":
      return <Notice>{strings.enter}</Notice>;
    case "zero":
      return <ErrorNotice>{strings.zero}</ErrorNotice>;
    case "negative":
      return <ErrorNotice>{strings.negative}</ErrorNotice>;
    case "too-large":
      return <ErrorNotice>{strings.tooLarge}</ErrorNotice>;
    default:
      return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
}

export default function NumberPropertiesCalculatorWidget() {
  const strings = useToolUi(NUMBER_PROPERTIES_CALCULATOR_STRINGS);
  const [raw, setRaw] = useState("496");
  const deferred = useDeferredValue(raw);
  return (
    <>
      <TextField
        hint={strings.numberHint}
        id="npc-number"
        label={strings.number}
        mono
        numeric
        onChange={setRaw}
        value={raw}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome raw={deferred} strings={strings} />
      </section>
    </>
  );
}
